type AnalyticsData = Record<string, string | number | boolean>;

// Campaign links must use one of these exact, non-personal utm_campaign labels.
// Add a new label here only after checking that it cannot encode a person or contact.
const CAMPAIGNS = ["launch", "retail_growth", "referral_push", "holiday_promo"] as const;
type Campaign = (typeof CAMPAIGNS)[number];
const CAMPAIGN_KEY = "retailpulse_trial_campaign";

function validCampaign(value: string | null): Campaign | null {
  return CAMPAIGNS.find(campaign => campaign === value) ?? null;
}

// A new homepage visit resets last-touch attribution, even when its URL has no campaign.
// Only a fixed label is kept, in this tab only; never persist URLs or other parameters.
export function captureLandingCampaign(search: string): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(search);
    const values = params.getAll("utm_campaign");
    const campaign = values.length === 1 ? validCampaign(values[0]) : null;
    if (campaign) window.sessionStorage.setItem(CAMPAIGN_KEY, campaign);
    else window.sessionStorage.removeItem(CAMPAIGN_KEY);
  } catch {
    // Storage restrictions and malformed input must not block a visit.
  }
}

function signupCampaign(): Campaign | "unattributed" {
  if (typeof window === "undefined") return "unattributed";
  try {
    return validCampaign(window.sessionStorage.getItem(CAMPAIGN_KEY)) ?? "unattributed";
  } catch {
    return "unattributed";
  }
}

declare global {
  interface Window {
    umami?: {
      track(name: string, data?: AnalyticsData): void;
    };
  }
}

export function trackEvent(name: string, data?: AnalyticsData): void {
  if (typeof window === "undefined") return;

  try {
    // Replit injects the analytics collector on published apps when Project Analytics
    // is enabled in Publishing settings. It is intentionally absent in the dev preview.
    // Do not add another script here: page views and custom events use the same collector.
    window.umami?.track(name, data);
  } catch {
    // Analytics must never interrupt a user workflow.
  }
}

export function trackHomepageAction(action: string, location: string, plan?: string): void {
  trackEvent("homepage_action_clicked", {
    action,
    location,
    ...(plan ? { plan } : {}),
    ...(action === "start_trial" ? { campaign: signupCampaign() } : {}),
  });
}

// One tracker per signup visit. Only fixed step labels are sent, never form contents.
// Funnel: homepage_action_clicked (action=start_trial) → signup_started →
// signup_step_reached (industry, email_verification) → signup_completed.
export function createSignupFunnelTracker() {
  const seen = new Set<string>();
  const once = (key: string, name: string, data?: AnalyticsData) => {
    if (seen.has(key)) return;
    seen.add(key);
    trackEvent(name, data);
  };

  return {
    start: () => once("started", "signup_started"),
    reach: (step: "industry" | "email_verification") =>
      once(step, "signup_step_reached", { step }),
    complete: (industry: string, referralUsed: boolean) => {
      if (seen.has("completed")) return;
      once("completed", "signup_completed", {
        industry,
        referral_used: referralUsed,
        campaign: signupCampaign(),
      });
      try {
        window.sessionStorage.removeItem(CAMPAIGN_KEY);
      } catch {
        // A completed signup must not fail because storage is unavailable.
      }
    },
  };
}